import React, { useEffect, useMemo, useState } from "react";
import { useGetCodesQuery } from "actions/comptabilite/queries";
import ModalAjouterCompteComptable from "components/ModalAjouterCompteComptable";
import ModalImporterPlanComptable from "components/ModalImporterPlanComptable";
import TableLoader from "components/Loader/TableLoader";
import { useDossierRole } from "contexts/DossierRoleContext";

const C = {
  bg: "#f0f5f6",
  surface: "#ffffff",
  border: "#c8d8db",
  borderSoft: "#e2eced",
  teal: "#009688",
  tealDark: "#006b78",
  tealDim: "rgba(0,143,160,0.08)",
  text: "#0d2226",
  textDim: "#3d6068",
  textMuted: "#7da5ad",
  mono: '"DM Mono","Courier New",monospace',
  display: '"Playfair Display",Georgia,serif',
  sans: '"DM Sans",system-ui,sans-serif',
};

const S = {
  wrapper: {
    fontFamily: C.sans,
    background: C.bg,
    color: C.text,
    padding: "28px 20px 60px",
    minHeight: "100vh",
  },
  inner: { maxWidth: "1120px", margin: "0 auto" },
  headerRow: { display: "flex", alignItems: "center", gap: "12px", marginBottom: "6px", flexWrap: "wrap" },
  title: { fontFamily: C.display, fontSize: "1.6rem", fontWeight: 700, margin: 0, color: C.text },
  badge: {
    fontFamily: C.mono,
    fontSize: "9px",
    letterSpacing: "2px",
    textTransform: "uppercase",
    color: C.teal,
    background: C.tealDim,
    border: "1px solid rgba(0,143,160,0.25)",
    padding: "3px 12px",
    borderRadius: "100px",
  },
  divider: {
    height: "1px",
    background: `linear-gradient(90deg, ${C.border}, transparent)`,
    marginTop: "8px",
    marginBottom: "18px",
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "10px",
    flexWrap: "wrap",
    marginBottom: "12px",
  },
  searchInput: {
    minWidth: "280px",
    width: "min(100%, 460px)",
    border: `1px solid ${C.border}`,
    borderRadius: "8px",
    padding: "8px 10px",
    outline: "none",
    fontSize: "13px",
    fontFamily: C.sans,
    color: C.text,
    background: C.surface,
  },
  pageSizeSelect: {
    border: `1px solid ${C.border}`,
    borderRadius: "8px",
    padding: "8px 10px",
    outline: "none",
    fontSize: "12px",
    fontFamily: C.mono,
    color: C.textDim,
    background: C.surface,
  },
  addButton: {
    border: "none",
    background: C.teal,
    color: "#fff",
    borderRadius: "8px",
    padding: "9px 16px",
    cursor: "pointer",
    fontSize: "12px",
    fontWeight: 600,
    fontFamily: C.sans,
    display: "flex",
    alignItems: "center",
    gap: "6px",
    transition: "all 0.2s ease",
    boxShadow: "0 2px 8px rgba(0,143,160,0.2)",
  },
  card: {
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: "10px",
    overflow: "hidden",
    boxShadow: "0 2px 16px rgba(0,143,160,0.08)",
  },
  tableWrap: {
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
  },
  table: {
    width: "100%",
    minWidth: "760px",
    borderCollapse: "collapse",
    fontSize: "12px",
  },
  th: (align = "left") => ({
    fontFamily: C.mono,
    fontSize: "10px",
    fontWeight: 600,
    letterSpacing: "1.4px",
    textTransform: "uppercase",
    backgroundColor: C.teal,
    color: "#000",
    textAlign: align,
    padding: "10px 12px",
    borderBottom: `2px solid ${C.tealDark}`,
    whiteSpace: "nowrap",
  }),
  td: (align = "left", extra = {}) => ({
    padding: "9px 12px",
    borderBottom: `1px solid ${C.borderSoft}`,
    textAlign: align,
    fontFamily: C.sans,
    fontSize: "13px",
    color: C.text,
    verticalAlign: "middle",
    textTransform: "uppercase",
    ...extra,
  }),
  code: {
    fontFamily: C.mono,
    fontSize: "12px",
    fontWeight: 700,
    color: C.tealDark,
  },
  customBadge: {
    display: "inline-block",
    fontFamily: C.sans,
    fontSize: "9px",
    fontWeight: 600,
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    color: "#fff",
    background: C.teal,  // ✨ Utiliser la couleur principale de la plateforme
    borderRadius: "4px",
    padding: "2px 6px",
    marginLeft: "8px",
    boxShadow: `0 2px 4px ${C.teal}40`,  // ✨ Ombre avec la couleur teal
  },
  classBadge: {
    display: "inline-block",
    fontFamily: C.mono,
    fontSize: "10px",
    color: C.textDim,
    background: C.tealDim,
    border: `1px solid ${C.border}`,
    borderRadius: "4px",
    padding: "1px 7px",
  },
  empty: {
    textAlign: "center",
    color: C.textMuted,
    fontFamily: C.mono,
    fontSize: "12px",
    padding: "44px 12px",
    letterSpacing: "1px",
  },
  pagination: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "10px",
    flexWrap: "wrap",
    marginTop: "12px",
  },
  pageBtn: {
    border: `1px solid ${C.border}`,
    background: C.surface,
    borderRadius: "8px",
    padding: "7px 10px",
    cursor: "pointer",
    fontFamily: C.mono,
    fontSize: "11px",
  },
  pageMeta: {
    fontFamily: C.mono,
    fontSize: "11px",
    color: C.textDim,
  },
  loader: {
    textAlign: "center",
    padding: "60px",
    fontFamily: C.mono,
    color: C.textMuted,
    letterSpacing: "2px",
    fontSize: "12px",
  },
};

const normalizeCodes = (raw) => {
  const rows = Array.isArray(raw)
    ? raw
    : Array.isArray(raw?.data)
      ? raw.data
      : Array.isArray(raw?.results)
        ? raw.results
        : [];

  const mapped = rows
    .map((item, idx) => {
      const combine = String(item?.combine || "").trim();
      let code = String(
        item?.code ??
          item?.numero ??
          item?.numero_compte ??
          item?.compte ??
          ""
      ).trim();
      let libelle = String(
        item?.libelle ??
          item?.intitule ??
          item?.label ??
          ""
      ).trim();

      if ((!code || !libelle) && combine) {
        const match = combine.match(/^(\d+[A-Za-z0-9]*)\s*[-–]?\s*(.*)$/);
        if (match) {
          if (!code) code = (match[1] || "").trim();
          if (!libelle) libelle = (match[2] || "").trim();
        }
      }

      if (!code && !libelle) return null;
      const classe = String(code || "").charAt(0) || "-";

      return {
        key: `${code}-${idx}`,
        code: code || "-",
        libelle: libelle || combine || "Sans libellé",
        classe,
        is_custom: item?.is_custom || false,  // ✨ Conserver le flag
      };
    })
    .filter(Boolean);

  // Dédupliquer les comptes par code (garder le premier)
  const uniqueMap = new Map();
  mapped.forEach((item) => {
    if (!uniqueMap.has(item.code)) {
      uniqueMap.set(item.code, item);
    }
  });

  const uniqueCodes = Array.from(uniqueMap.values());

  return uniqueCodes.sort((a, b) =>
    a.code.localeCompare(b.code, undefined, { numeric: true, sensitivity: "base" })
  );
};

const PlanComptable = ({ dossier_id, embedded = false }) => {
  const { canEdit } = useDossierRole();
  const [search, setSearch] = useState("");
  const [filterClasse, setFilterClasse] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  const { data: rawCodes, isLoading, isFetching, refetch } = useGetCodesQuery(
    { dossier_client: dossier_id },
    { skip: !dossier_id }
  );

  useEffect(() => {
    if (dossier_id) refetch();
  }, [dossier_id, refetch]);

  useEffect(() => {
    if (rawCodes) {
      console.log('📊 Raw codes reçus:', rawCodes);
      // Vérifier si les codes contiennent un plan_file_id
      const firstCode = Array.isArray(rawCodes) ? rawCodes[0] : rawCodes?.data?.[0] || rawCodes?.results?.[0];
      console.log('🔍 Premier compte:', firstCode);
    }
  }, [rawCodes]);

  const handleCompteCreated = () => {
    refetch(); // Recharger la liste après création
  };

  const allCodes = useMemo(() => normalizeCodes(rawCodes), [rawCodes]);

  const classesDisponibles = useMemo(() => {
    const set = new Set(allCodes.map((item) => item.classe).filter(Boolean));
    return Array.from(set).sort((a, b) =>
      String(a).localeCompare(String(b), undefined, { numeric: true })
    );
  }, [allCodes]);

  const filteredCodes = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = allCodes.filter((item) => {
      const matchesSearch = !q || (
        String(item.code).toLowerCase().includes(q) ||
        String(item.libelle).toLowerCase().includes(q) ||
        String(item.classe).toLowerCase().includes(q)
      );
      const matchesClasse = !filterClasse || String(item.classe) === String(filterClasse);
      return matchesSearch && matchesClasse;
    });
    
    // ✨ TRIER : Comptes personnalisés EN PREMIER
    const custom = filtered.filter(item => item.is_custom);
    const standard = filtered.filter(item => !item.is_custom);
    
    // Trier chaque groupe par code
    custom.sort((a, b) => String(a.code).localeCompare(String(b.code), undefined, { numeric: true }));
    standard.sort((a, b) => String(a.code).localeCompare(String(b.code), undefined, { numeric: true }));
    
    // Comptes personnalisés en premier
    return [...custom, ...standard];
  }, [allCodes, search, filterClasse]);

  const totalPages = Math.max(1, Math.ceil(filteredCodes.length / pageSize));
  const paginatedCodes = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredCodes.slice(start, start + pageSize);
  }, [filteredCodes, currentPage, pageSize]);

  const firstItem = filteredCodes.length === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const lastItem = Math.min(currentPage * pageSize, filteredCodes.length);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, filterClasse, pageSize, allCodes.length]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  if (isLoading || isFetching) {
    return (
      <div style={S.wrapper}>
        <div style={S.inner}>
          <div style={{ ...S.card, padding: "30px" }}>
            <TableLoader
              message="Chargement du plan comptable..."
              subMessage="Récupération des comptes et paramétrages SYSCOHADA"
              minHeight="350px"
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={S.wrapper}>
      <div style={S.inner}>
        <div style={S.headerRow}>
          {!embedded && <h2 style={S.title}>Plan Comptable</h2>}
          <span style={S.badge}>Comptes utilisés</span>
        </div>
        <div style={S.divider} />

        <div style={S.toolbar}>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", width: "100%" }}>
            <input
              type="text"
              style={S.searchInput}
              placeholder="Rechercher un compte (numéro, libellé, classe...)"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select
              value={filterClasse}
              onChange={(e) => setFilterClasse(e.target.value)}
              style={S.pageSizeSelect}
            >
              <option value="">Toutes les classes</option>
              {classesDisponibles.map((classe) => (
                <option key={classe} value={classe}>
                  Classe {classe}
                </option>
              ))}
            </select>
          </div>
          {canEdit && (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <button
                type="button"
                className="btn btn-outline-secondary"
                style={{ borderRadius: "31px", padding: "8px 16px", fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", opacity: dossier_id ? 1 : 0.5, cursor: dossier_id ? 'pointer' : 'not-allowed' }}
                onClick={() => dossier_id && setIsImportModalOpen(true)}
                disabled={!dossier_id}
                title={dossier_id ? "Importer un plan comptable depuis un fichier Excel" : "Aucun dossier sélectionné"}
              >
                ⬆ Importer un plan
              </button>
              <button
                type="button"
                className="btn"
                style={{ background: "#009688", color: "#fff", borderRadius: "31px", padding: "8px 16px", fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", border: "none", opacity: dossier_id ? 1 : 0.5, cursor: dossier_id ? 'pointer' : 'not-allowed' }}
                onClick={() => dossier_id && setIsModalOpen(true)}
                disabled={!dossier_id}
                title={dossier_id ? "Ajouter un nouveau compte" : "Aucun dossier sélectionné"}
              >
                + Ajouter un compte
              </button>
            </div>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={S.pageMeta}>Lignes</span>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              style={S.pageSizeSelect}
            >
              {[10, 20, 50, 100].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div style={S.card}>
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th("left")}>N° Compte</th>
                  <th style={S.th("left")}>Libellé</th>
                  <th style={S.th("center")}>Classe</th>
                </tr>
              </thead>
              <tbody>
                {paginatedCodes.length === 0 ? (
                  <tr>
                    <td colSpan={3} style={S.empty}>
                      AUCUN COMPTE TROUVÉ
                    </td>
                  </tr>
                ) : (
                  paginatedCodes.map((item, idx) => (
                    <tr key={item.key} style={{ background: idx % 2 ? "#f8fbfc" : "#fff" }}>
                      <td style={S.td("left")}>
                        <span style={S.code}>{item.code}</span>
                        {item.is_custom && (
                          <span style={S.customBadge}>
                            Personnalisé
                          </span>
                        )}
                      </td>
                      <td style={S.td("left")}>{item.libelle}</td>
                      <td style={S.td("center")}>
                        <span style={S.classBadge}>Classe {item.classe}</span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div style={S.pagination}>
          <span style={S.pageMeta}>
            {filteredCodes.length === 0
              ? "0 sur 0"
              : `${firstItem} - ${lastItem} sur ${filteredCodes.length}`}
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button
              type="button"
              style={S.pageBtn}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage <= 1}
            >
              Précédent
            </button>
            <span style={S.pageMeta}>
              Page {currentPage}/{totalPages}
            </span>
            <button
              type="button"
              style={S.pageBtn}
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
            >
              Suivant
            </button>
          </div>
        </div>

        {/* Modal d'ajout de compte comptable */}
        {dossier_id && (
          <ModalAjouterCompteComptable
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            plan_file_id={dossier_id}
            onSuccess={handleCompteCreated}
          />
        )}

        {/* Modal d'import du plan comptable */}
        {dossier_id && (
          <ModalImporterPlanComptable
            isOpen={isImportModalOpen}
            onClose={() => setIsImportModalOpen(false)}
            dossier_id={dossier_id}
            onSuccess={handleCompteCreated}
          />
        )}
      </div>
    </div>
  );
};

export default PlanComptable;
